import { useState, useEffect, useRef } from "react";
import {
    ImageIcon,
    NewspaperIcon,
    MenuIcon,
    SunIcon,
    MoonIcon,
    LogOutIcon,
    XIcon,
} from "lucide-react";
import Images from "./ImageLists";
import { useNavigate } from "react-router-dom";
import Users from "./Users";

const Dashboard = () => {
    const [activeTab, setActiveTab] = useState("images");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(false);
    const sidebarRef = useRef(null);
    const navigate = useNavigate();

    // Sync dark mode with document
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    }, [darkMode]);

    // Close sidebar on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setSidebarOpen(false);
            }
        };

        if (sidebarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [sidebarOpen]);

    return (
        <div className="flex min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white relative">
            {/* Sidebar */}
            <div
                ref={sidebarRef}
                className={`md:w-64 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-5 fixed top-0 left-0 bottom-0 z-50 transition-transform transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
                    } md:translate-x-0`}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Dashboard</h2>
                    <button
                        className="md:hidden text-gray-700 dark:text-white"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <XIcon size={24} />
                    </button>
                </div>

                <nav className="flex flex-col space-y-4">
                    <button
                        onClick={() => {
                            setActiveTab("images");
                            
                        }}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md transition ${activeTab === "images"
                            ? "bg-blue-600 text-white"
                            : "hover:bg-gray-200 dark:hover:bg-gray-700"
                            }`}
                    >
                        <ImageIcon size={24} />
                        <span>Images</span>
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab("users");

                        }}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md transition ${activeTab === "users"
                            ? "bg-blue-600 text-white"
                            : "hover:bg-gray-200 dark:hover:bg-gray-700"
                            }`}
                    >
                        <NewspaperIcon size={24} />
                        <span>Users</span>
                    </button>

                </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 md:ml-64 w-full">
                {/* Top Navbar */}
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 px-4 py-3 shadow-md sticky top-0 z-40">
                    <div className="md:hidden">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="text-gray-700 dark:text-white"
                        >
                            <MenuIcon size={24} />
                        </button>
                    </div>

                    <h1 className="text-lg font-semibold">Visiga Website</h1>

                    <div className="flex items-center space-x-4">
                        {/* Logout */}
                        <button
                            onClick={() => {
                                localStorage.removeItem("accessToken");
                                localStorage.removeItem("refreshToken");
                                navigate("/login");
                            }}
                            className="flex items-center space-x-2 px-3 py-2 rounded-md bg-red-500 text-white hover:bg-red-600"
                        >
                            <LogOutIcon size={18} />
                            <span className="text-sm">Logout</span>
                        </button>

                        {/* Change Mode Button */}
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className="flex items-center space-x-2 px-3 py-2 rounded-md bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                        >
                            {darkMode ? <SunIcon size={18} /> : <MoonIcon size={18} />}
                            <span className="text-sm">
                                {darkMode ? "Light Mode" : "Dark Mode"}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="p-4">
                    {activeTab === "images" && <Images />}
                    {activeTab === "users" && <Users />}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
